import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./MediaDisplay.module.css";
import IconsProvider from "./MaterialSymbols";

const MediaViewer = ({ media, onClose, title, currentIndex = 0 }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(currentIndex);
  const [isClosing, setIsClosing] = useState(false);
  const [thumbnailRefs, setThumbnailRefs] = useState({});
  const viewerRef = useRef(null);
  const thumbnailsContainerRef = useRef(null);

  const handleThumbnailClick = (index) => {
    setCurrentMediaIndex(index);
  };

  const handleOutsideClick = (e) => {
    if (viewerRef.current && !viewerRef.current.contains(e.target)) {
      startCloseAnimation();
    }
  };

  const handleCloseClick = () => {
    startCloseAnimation();
  };

  const startCloseAnimation = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // 初始化缩略图refs
  useEffect(() => {
    const refs = {};
    media.forEach((_, index) => {
      refs[index] = React.createRef();
    });
    setThumbnailRefs(refs);
  }, [media]);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const currentMedia = media[currentMediaIndex];

  return createPortal(
    <div
      className={`${styles.mediaViewerOverlay} ${
        isClosing ? styles.closing : ""
      }`}
    >
      <div
        ref={viewerRef}
        className={`${styles.mediaViewer} ${isClosing ? styles.closing : ""}`}
      >
        <button className={styles.closeButton} onClick={handleCloseClick}>
          <IconsProvider>close</IconsProvider>
        </button>

        <div className={styles.viewerContent}>
          <div className={styles.mainImageContainer}>
            {currentMedia.type === "video" ? (
              <video
                src={currentMedia.src}
                className={styles.mainMedia}
                autoPlay
                muted
                playsInline
              />
            ) : (
              <img
                src={currentMedia.src}
                alt="Media preview"
                className={styles.mainMedia}
              />
            )}
          </div>

          <div className={styles.rightPanel}>
            <h3>{title}</h3>
            <div className={styles.thumbnailsList} ref={thumbnailsContainerRef}>
              {media.map((item, index) => (
                <div
                  key={index}
                  ref={thumbnailRefs[index]}
                  className={`${styles.viewerThumbnail} ${
                    index === currentMediaIndex ? styles.active : ""
                  }`}
                  onClick={() => handleThumbnailClick(index)}
                >
                  <div className={styles.thumbnailWrapper}>
                    {item.type === "video" ? (
                      <div className={styles.videoThumbnail}>
                        <video
                          src={item.src}
                          className={styles.thumbnailMedia}
                          onLoadedMetadata={(e) => {
                            const video = e.target;
                            const container = thumbnailRefs[index]?.current;
                            if (container && video) {
                              const aspectRatio =
                                video.videoWidth / video.videoHeight;
                              const width = container.offsetWidth;
                              const height = width / aspectRatio;
                              container.style.height = `${height}px`;
                            }
                          }}
                        />
                        <div className={styles.videoIcon}>
                          <IconsProvider>play_circle</IconsProvider>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={item.src}
                        alt={`Thumbnail ${index + 1}`}
                        className={styles.thumbnailMedia}
                        onLoad={(e) => {
                          const img = e.target;
                          const container = thumbnailRefs[index]?.current;
                          if (container && img) {
                            const aspectRatio =
                              img.naturalWidth / img.naturalHeight;
                            const width = container.offsetWidth;
                            const height = width / aspectRatio;
                            container.style.height = `${height}px`;
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const MediaDisplay = ({ project, projectIndex }) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const videoRefs = useRef({});

  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `
      .media-viewer-open {
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      if (styleTag.parentNode) {
        document.head.removeChild(styleTag);
      }
    };
  }, []);

  useEffect(() => {
    if (isViewerOpen) {
      document.body.classList.add("media-viewer-open");
    } else {
      document.body.classList.remove("media-viewer-open");
    }
  }, [isViewerOpen]);

  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  
  const handleViewMoreClick = (e) => {
    e.stopPropagation();
    setIsViewerOpen(true);
  };

  const handleMediaClick = (index) => {
    setSelectedMediaIndex(index);
    setIsViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setIsViewerOpen(false);
  };

  if (!project.media?.length) return null;

  const images = project.media.filter((media) => media.type === "image");
  const videos = project.media.filter((media) => media.type === "video");

  const allMedia = [...images, ...videos];

  const hasMoreMedia = allMedia.length > 4;

  const displayMedia = allMedia.length === 4 ? allMedia : allMedia.slice(0, 3);

  return (
    <>
      <div className={styles.mediaContainer}>
        <div className={styles.mediaGrid}>
          {displayMedia.map((media, index) => {
            const itemClassName =
              index === 0
                ? styles.mainMediaItem
                : index === 1
                ? styles.secondMediaItem
                : styles.thirdMediaItem;

            return (
              <div 
                key={`media-${index}`} 
                className={itemClassName} 
                onClick={() => handleMediaClick(index)}
                style={{ cursor: 'pointer' }}
              >
                {media.type === "video" ? (
                  <div className={styles.videoContainer}>
                    <video
                      ref={(el) => {
                        videoRefs.current[index] = el;
                      }}
                      src={media.src}
                      muted
                      playsInline
                      className={styles.projectMedia}
                    />
                    <div className={styles.videoIcon}>
                      <IconsProvider iconSize="30px">play_circle</IconsProvider>
                    </div>
                  </div>
                ) : (
                  <img
                    src={media.src}
                    alt={`${project.title} showcase ${index + 1}`}
                    className={styles.projectMedia}
                  />
                )}
              </div>
            );
          })}

          {allMedia.length === 4 ? (
            <div 
              key="media-3" 
              className={styles.fourthMediaItem}
              onClick={() => handleMediaClick(3)}
              style={{ cursor: 'pointer' }}
            >
              {allMedia[3].type === "video" ? (
              <div className={styles.videoContainer}>
                <video
                  ref={(el) => {
                    videoRefs.current[3] = el;
                  }}
                  src={allMedia[3].src}
                  muted
                  playsInline
                  className={styles.projectMedia}
                />
                <div className={styles.videoIcon}>
                  <IconsProvider iconSize="30px">play_circle</IconsProvider>
                </div>
              </div>
              ) : (
                <img
                  src={allMedia[3].src}
                  alt={`${project.title} showcase 4`}
                  className={styles.projectMedia}
                />
              )}
            </div>
          ) : (
            hasMoreMedia && (
              <div
                className={styles.thumbnailsContainer}
                onClick={handleViewMoreClick}
              >
                {/* 显示"View More"按钮和缩略图 */}
                <div className={styles.viewMoreContent}>
                  <div className={styles.viewMoreThumbnails}>
                    {allMedia
                      .slice(3, Math.min(6, allMedia.length))
                      .map((media, index) => (
                        <div
                          key={`thumb-${index}`}
                          className={styles.viewMoreThumbnail}
                        >
                          {media.type === "video" ? (
                            <video
                              src={media.src}
                              className={styles.thumbnailMedia}
                            />
                          ) : (
                            <img
                              src={media.src}
                              alt={`${project.title} thumbnail ${index + 1}`}
                              className={styles.thumbnailMedia}
                            />
                          )}
                        </div>
                      ))}
                  </div>
                  <div className={styles.viewMoreInfo}>
                    <IconsProvider>photo_library</IconsProvider>
                    <span style={{ userSelect: "none" }}>View More</span>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
      {isViewerOpen && (
        <MediaViewer
          media={allMedia}
          onClose={handleCloseViewer}
          title={project.title}
          currentIndex={selectedMediaIndex}
        />
      )}
    </>
  );
};

export default MediaDisplay;
